<template>
  <div
    :class="bare ? '' : 'bg-white border h-100'"
    :style="bare ? '' : 'border-radius:12px; border-color:#e9ecef !important; padding:1.25rem;'"
  >

    <!-- Toolbar -->
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:10px;">
      <div v-if="!bare" class="d-flex align-items-baseline" style="gap:8px;">
        <span class="font-weight-500 text-dark" style="font-size:14px;">Sales statistics</span>
        <span class="text-secondary" style="font-size:12px;">{{ periodLabel }}</span>
      </div>

      <div class="d-flex align-items-center flex-wrap ml-auto" style="gap:8px;">
        <!-- Period selector -->
        <div class="d-flex bg-light p-1" style="border-radius:8px; gap:2px;">
          <button
            v-for="p in periods"
            :key="p.value"
            @click="activePeriod = p.value"
            class="btn btn-sm border-0"
            :class="activePeriod === p.value ? 'bg-white text-dark' : 'bg-transparent text-secondary'"
            style="font-size:11px; font-weight:500; border-radius:6px; padding:4px 10px;"
          >
            {{ p.label }}
          </button>
        </div>

        <!-- Chart type selector -->
        <div class="d-flex bg-light p-1" style="border-radius:8px; gap:2px;">
          <button
            v-for="ct in chartTypes"
            :key="ct.value"
            @click="activeChartType = ct.value"
            class="btn btn-sm border-0 d-inline-flex align-items-center"
            :class="activeChartType === ct.value ? 'bg-white text-dark' : 'bg-transparent text-secondary'"
            style="font-size:11px; font-weight:500; border-radius:6px; padding:4px 10px; gap:4px;"
          >
            <i :class="ct.icon" style="font-size:10px;"></i>
            {{ ct.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Metric tabs -->
    <div class="d-flex mb-3" style="border-bottom:0.5px solid #e9ecef; gap:0; overflow-x:auto;">
      <button
        v-for="m in metrics"
        :key="m.value"
        @click="activeMetric = m.value"
        class="btn btn-sm border-0 bg-transparent"
        :class="activeMetric === m.value ? 'text-dark font-weight-500' : 'text-secondary'"
        style="font-size:12px; padding:6px 14px; border-radius:0; white-space:nowrap; flex-shrink:0;"
        :style="activeMetric === m.value ? 'border-bottom: 2px solid #1e293b; margin-bottom: -1px;' : 'border-bottom: 2px solid transparent; margin-bottom: -1px;'"
      >
        {{ m.label }}
      </button>
    </div>

    <!-- Legend -->
    <div class="d-flex flex-wrap mb-3" style="gap:14px;">
      <div
        v-for="ds in activeLegend"
        :key="ds.label"
        class="d-inline-flex align-items-center"
        style="gap:5px; font-size:11px; color:#64748b;"
      >
        <span
          class="d-inline-block"
          :style="`width:10px;height:10px;border-radius:2px;background:${ds.color};`"
        ></span>
        {{ ds.label }}
      </div>
    </div>

    <!-- Chart -->
    <div class="position-relative" style="height:300px;">
      <canvas ref="chartCanvas"></canvas>
    </div>

  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'SalesChartPanel',

  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    bare: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      activePeriod: 'week',
      activeChartType: 'line',
      activeMetric: 'sales',

      periods: [
        { value: 'day',   label: 'Day'   },
        { value: 'week',  label: 'Week'  },
        { value: 'month', label: 'Month' }
      ],

      chartTypes: [
        { value: 'line', label: 'Line', icon: 'bi bi-graph-up'     },
        { value: 'bar',  label: 'Bar',  icon: 'bi bi-bar-chart'    },
        { value: 'area', label: 'Area', icon: 'bi bi-area-chart'   }
      ],

      metrics: [
        { value: 'sales',  label: 'Gross sales'      },
        { value: 'net',    label: 'Net sales'         },
        { value: 'invoices', label: 'Invoices'        },
        { value: 'orders', label: 'Orders'            },
        { value: 'profit', label: 'Gross profit'      },
        { value: 'aov',    label: 'Avg order value'   }
      ],

      metricConfig: {
        sales:  { label: 'Gross sales',       color: '#1e293b', prefix: 'KES ' },
        net:    { label: 'Net sales',          color: '#94a3b8', prefix: 'KES ' },
        invoices: { label: 'Invoice sales',    color: '#1e293b', prefix: 'KES ' },
        orders: { label: 'Orders',             color: '#1e293b', prefix: ''     },
        profit: { label: 'Gross profit',       color: '#1e293b', prefix: 'KES ' },
        aov:    { label: 'Avg order value',    color: '#1e293b', prefix: 'KES ' }
      }
    };
  },

  computed: {
    periodLabel() {
      return { day: 'Today', week: 'This week', month: 'This year' }[this.activePeriod];
    },

    currentData() {
      return this.data[this.activePeriod] || { labels: [], sales: [], net: [], orders: [], profit: [], aov: [], invoices: [] };
    },

    activeLegend() {
      if (this.activeMetric === 'sales') {
        return [
          { label: 'Gross sales', color: '#1e293b' },
          { label: 'Net sales',   color: '#94a3b8' }
        ];
      }
      const m = this.metricConfig[this.activeMetric];
      return [{ label: m.label, color: m.color }];
    }
  },

  watch: {
    activePeriod()    { this.refreshChart(); },
    activeChartType() { this.refreshChart(); },
    activeMetric()    { this.refreshChart(); },
    data:             { handler() { this.refreshChart(); }, deep: true }
  },

  mounted() {
    this.initChart();
  },

  beforeUnmount() {
    if (this.chart) this.chart.destroy();
  },

  methods: {
    buildDatasets() {
      const d = this.currentData;
      const ct = this.activeChartType;
      const isBar = ct === 'bar';
      const isArea = ct === 'area';

      const makeDs = (key, label, color, bgAlpha) => ({
        label,
        data: d[key] || [],
        borderColor: color,
        backgroundColor: isArea
          ? color.replace(')', `, ${bgAlpha})`).replace('rgb', 'rgba')
          : isBar ? color : color,
        borderWidth: 1.5,
        fill: isArea,
        tension: 0.35,
        pointRadius: isBar ? 0 : 3,
        pointHoverRadius: isBar ? 0 : 5,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        borderRadius: isBar ? 4 : 0
      });

      if (this.activeMetric === 'sales') {
        return [
          { ...makeDs('sales', 'Gross sales', '#1e293b', 0.08), backgroundColor: isBar ? 'rgba(30,41,59,0.85)' : isArea ? 'rgba(30,41,59,0.08)' : '#1e293b' },
          { ...makeDs('net',   'Net sales',   '#94a3b8', 0.06), backgroundColor: isBar ? 'rgba(148,163,184,0.7)': isArea ? 'rgba(148,163,184,0.06)': '#94a3b8' }
        ];
      }

      const m = this.metricConfig[this.activeMetric];
      return [{
        ...makeDs(this.activeMetric, m.label, m.color, 0.08),
        backgroundColor: isBar ? 'rgba(30,41,59,0.85)' : isArea ? 'rgba(30,41,59,0.08)' : m.color
      }];
    },

    formatValue(v) {
      const prefix = this.metricConfig[this.activeMetric]?.prefix || '';
      if (v >= 1000000) return prefix + (v / 1000000).toFixed(1) + 'M';
      if (v >= 1000)    return prefix + (v / 1000).toFixed(0) + 'K';
      return prefix + Math.round(v).toLocaleString('en-US');
    },

    initChart() {
      const ctx = this.$refs.chartCanvas.getContext('2d');
      const type = this.activeChartType === 'area' ? 'line' : this.activeChartType;

      this.chart = new Chart(ctx, {
        type,
        data: {
          labels: this.currentData.labels || [],
          datasets: this.buildDatasets()
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 200 },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1e293b',
              titleColor: '#fff',
              bodyColor: 'rgba(255,255,255,0.7)',
              padding: 10,
              cornerRadius: 6,
              displayColors: true,
              callbacks: {
                label: (ctx) => ` ${this.formatValue(ctx.parsed.y)}`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: { font: { size: 11 }, color: '#94a3b8' }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.04)' },
              border: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#94a3b8',
                callback: (v) => this.formatValue(v)
              }
            }
          },
          interaction: { intersect: false, mode: 'index' }
        }
      });
    },

    refreshChart() {
      if (!this.chart) return;
      const type = this.activeChartType === 'area' ? 'line' : this.activeChartType;
      this.chart.config.type = type;
      this.chart.data.labels = this.currentData.labels || [];
      this.chart.data.datasets = this.buildDatasets();
      this.chart.options.plugins.tooltip.callbacks.label = (ctx) => ` ${this.formatValue(ctx.parsed.y)}`;
      this.chart.options.scales.y.ticks.callback = (v) => this.formatValue(v);
      this.chart.update();
    }
  }
};
</script>
